import React, { useState, useMemo } from "react";
import { useNavigate } from "../../hooks/useRouter";
import "./MyApps.css";
import { useConstants } from "../../constant/constantProvider";
import { useAccessibleApps } from "../../hooks/useAccessibleApps";
import { sanitizeTextInput } from "../../utils/textUtils";
import { RBAC_APPS } from "../../constant/rbacConfig";

import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import SearchBar from "../../components/SearchBar/SearchBar";

// assets
import arrowIcon from "../../assets/new-images/up-arrow-icon.svg";

function Apps() {
  const navigate = useNavigate();
  const { accessibleApps: visibleApps, isLoaded } = useAccessibleApps();
  const {
    MY_APPS, MY_APPS_SUBTITLE, SEARCH_APPS_PLACEHOLDER, NO_APPS_FOUND, OPEN,
    CATEGORY_CHATBOT, CATEGORY_ANALYTICS, CATEGORY_TRANSLATION, CATEGORY_GMT,
    CATEGORY_HR, CATEGORY_INSIGHTS, CATEGORY_ADMIN, CATEGORY_APP, CATEGORY_INTERVIEW
  } = useConstants();
  const [searchQuery, setSearchQuery] = useState("");

  const appThemes = useMemo(() => ({
    [RBAC_APPS.CHAT_GENIE]: { category: CATEGORY_CHATBOT, color: "yellow" },
    [RBAC_APPS.PROCUREMENT]: { category: CATEGORY_ANALYTICS, color: "blue" },
    [RBAC_APPS.TRANSLATION]: { category: CATEGORY_TRANSLATION, color: "purple" },
    [RBAC_APPS.GMT]: { category: CATEGORY_GMT, color: "orange" },
    [RBAC_APPS.HR_ASSISTANT]: { category: CATEGORY_HR, color: "cyan" },
    [RBAC_APPS.DEEP_INSIGHTS]: { category: CATEGORY_INSIGHTS, color: "orange" },
    "user-management": { category: CATEGORY_ADMIN, color: "gray" },
    [RBAC_APPS.TALENT_PULSE_360]: { category: CATEGORY_INTERVIEW, color: "light-purple" },
  }), [CATEGORY_CHATBOT, CATEGORY_ANALYTICS, CATEGORY_TRANSLATION, CATEGORY_GMT, CATEGORY_HR, CATEGORY_INSIGHTS, CATEGORY_ADMIN, CATEGORY_INTERVIEW]);

  const handleGoToApp = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/chat');
    }
  };

  const filteredApps = useMemo(() => {
    if (!searchQuery) return visibleApps;
    const q = searchQuery.trim().toLowerCase();
    return visibleApps.filter((app) =>
      app.title.toLowerCase().includes(q) ||
      app.description.toLowerCase().includes(q)
    );
  }, [visibleApps, searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  if (!isLoaded) {
    return <LoadingScreen isLoading={!isLoaded}></LoadingScreen>;
  }

  return (
    <div className="apps-page-container">
      <div className="apps-header-centered">
        <div className="apps-top-row">
          <button className="button-style back-icon" onClick={handleGoToApp} type="button">
            <img
              src={arrowIcon}
              alt="back"
              className="heading-arrow-icon"
            />
          </button>
          <h1 className="apps-main-title">{MY_APPS}</h1>
          <div className="spacer"></div>
        </div>
        <p className="apps-subtitle">{MY_APPS_SUBTITLE}</p>
        <SearchBar
          value={searchQuery}
          onChange={(val) => setSearchQuery(sanitizeTextInput(val))}
          onClear={handleClearSearch}
          placeholder={SEARCH_APPS_PLACEHOLDER}
          className="myapps-search-bar"
        />
      </div>

      <div className="apps-content-wrapper thin-scroll">
        {filteredApps.length > 0 ? (
          <div className="apps-grid">
            {filteredApps.map((app) => {
              const theme =
                appThemes[app.rbacKey] ||
                appThemes[app.id] || { category: CATEGORY_APP, color: "blue" };

              return (
                <div
                  key={app.id}
                  className="app-card-new"
                  onClick={() => navigate(app.path)}
                >
                  <div className={`card-top-section theme-${theme.color}`}>
                    <div className="category-tag">{theme.category}</div>
                    <div className="card-icon-container">
                      <img src={app.icon} alt={app.title} />
                    </div>
                  </div>
                  <div className="card-bottom-section">
                    <div className="card-info">
                      <h3 className="card-title">{app.title}</h3>
                      <p className="card-description">{app.description}</p>
                    </div>
                    <div className="card-footer">
                      <button className="button-style primary-button open-app-btn" type="button">
                        {OPEN}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-apps-msg">
            {NO_APPS_FOUND || "No apps found"}
          </div>
        )}
      </div>
    </div>
  );
}

export default Apps;
